import React, { useState, useEffect } from 'react';
import {
  SearchOutlined,
  DownloadOutlined,
  PrinterOutlined,
  DeleteOutlined,
  EyeOutlined,
  FileTextOutlined,
  UserOutlined,
  PhoneOutlined,
  DollarOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  WalletOutlined,
  BarChartOutlined,
  PieChartOutlined
} from '@ant-design/icons';
import {
  Table,
  Input,
  Button,
  Space,
  Card,
  Typography,
  Tag,
  Tooltip,
  message,
  Modal,
  Popconfirm,
  Row,
  Col,
  DatePicker,
  Statistic,
  Descriptions,
  Select,
  Tabs,
  Empty,
  List,
  Avatar,
  Divider,
  Grid,
  Pagination
} from 'antd';
import * as XLSX from 'xlsx';
import { getReceipts, getReceiptById, deleteReceipt, getParties } from '../Services/Services';
import COMPANY_LOGO from '../Logo.png'
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TabPane } = Tabs;
const { useBreakpoint } = Grid;

const PaymentInReport = () => {
  const screens = useBreakpoint();
  const isMobile = !screens.md; // <768px

  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Party Wise States
  const [parties, setParties] = useState([]);
  const [partyWiseData, setPartyWiseData] = useState([]);
  const [partyReceipts, setPartyReceipts] = useState([]);
  const [showPartyModal, setShowPartyModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [partySearch, setPartySearch] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [receiptPage, setReceiptPage] = useState(1);
  const [partyPage, setPartyPage] = useState(1);

  const mobilePageSize = 5;

  // Fetch parties and receipts on load
  useEffect(() => {
    fetchParties();
    fetchReceipts();
  }, []);

  const fetchParties = async () => {
    try {
      const response = await getParties();
      setParties(response.results || response || []);
    } catch (error) {
      console.error('Error fetching parties:', error);
    }
  };

  const fetchReceipts = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (search.trim()) filters.search = search.trim();
      if (fromDate) filters.from_date = fromDate;
      if (toDate) filters.to_date = toDate;

      const response = await getReceipts(filters);

      if (response.success) {
        setReceipts(response.results || []);
        generatePartyWiseReport(response.results || []);
        if (response.results?.length === 0) {
          message.info("No receipts found");
        } else {
          message.success(`Found ${response.count || 0} receipts`);
        }
      } else {
        setReceipts([]);
        setPartyWiseData([]);
        message.error(response.message || 'Failed to fetch receipts');
      }
    } catch (error) {
      console.error('Error fetching receipts:', error);
      message.error('Failed to fetch receipts');
      setReceipts([]);
      setPartyWiseData([]);
    } finally {
      setLoading(false);
    }
  };

  // Generate Party Wise Report
  const generatePartyWiseReport = (receiptsData) => {
    const partyMap = new Map();

    receiptsData.forEach(receipt => {
      const partyName = receipt.received_from || receipt.party_name || 'Unknown Party';
      const partyId = receipt.party_id || receipt.id;

      if (partyMap.has(partyName)) {
        const existing = partyMap.get(partyName);
        existing.totalAmount += parseFloat(receipt.amount) || 0;
        existing.receiptCount += 1;
        existing.receipts.push(receipt);
        // Update last receipt date if newer
        if (receipt.receipt_date > existing.lastReceiptDate) {
          existing.lastReceiptDate = receipt.receipt_date;
        }
      } else {
        partyMap.set(partyName, {
          partyId: partyId,
          partyName: partyName,
          partyMobile: receipt.party_mobile || receipt.mobile_no || '-',
          totalAmount: parseFloat(receipt.amount) || 0,
          receiptCount: 1,
          receipts: [receipt],
          lastReceiptDate: receipt.receipt_date
        });
      }
    });

    const partyReport = Array.from(partyMap.values()).sort((a, b) => b.totalAmount - a.totalAmount);
    setPartyWiseData(partyReport);
  };

  const handleDateRangeChange = (dates, dateStrings) => {
    setDateRange(dates);
    setFromDate(dateStrings[0]);
    setToDate(dateStrings[1]);
  };

  const handleSearch = () => {
    fetchReceipts();
  };

  const handleReset = () => {
    // Reset all filter states
    setSearch('');
    setFromDate('');
    setToDate('');
    setDateRange(null);
    setPartySearch('');
    setActiveTab('all');

    // Fetch all receipts without filters after a short delay
    setTimeout(() => {
      fetchReceipts();
    }, 100);
  };

  const viewPartyDetails = (party) => {
    setPartyReceipts(party.receipts);
    setShowPartyModal(true);
  };

  // NEW FUNCTION: Print Party Wise Receipts Statement
  const printPartyWiseReceipts = (party) => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Helper function to format currency
    const formatCurrency = (amount) => {
      if (isNaN(amount) || amount === null || amount === undefined) return '0.00';
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    // Calculate totals
    const totalAmount = party.totalAmount;
    const totalReceipts = party.receiptCount;

    // Build receipt rows for all receipts
    let receiptsRows = '';
    party.receipts.forEach((receipt, index) => {
      const receiptDate = receipt.receipt_date || (receipt.date ? new Date(receipt.date).toLocaleDateString('en-IN') : '-');
      const description = receipt.narration || receipt.rec_category || 'Payment Received';

      receiptsRows += `
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 10px 8px; text-align: center;">${index + 1}</td>
          <td style="padding: 10px 8px;">
            <div style="font-weight: 600;">${receipt.receipt_no || 'Auto'}</div>
            <div style="font-size: 10px; color: #64748b; margin-top: 2px;">${description}</div>
          </td>
          <td style="padding: 10px 8px; text-align: center;">${receiptDate}</td>
          <td style="padding: 10px 8px; text-align: center;">
            <span style="background: #e8f5e9; padding: 4px 8px; border-radius: 12px; font-size: 11px;">${receipt.rec_mode || '-'}</span>
          </td>
          <td style="padding: 10px 8px; text-align: right; font-weight: 600; color: #2e7d32;">₹${formatCurrency(receipt.amount)}</td>
        </tr>
      `;
    });

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Party Statement - ${party.partyName}</title>
        <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700;800&family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            height: 100%;
            background: #eef2f5;
            font-family: 'Poppins', sans-serif;
          }
          body {
            padding: 20px;
            display: flex;
            justify-content: center;
          }
          
          /* Master Print Table Outer Frame */
          .print-master-table {
            width: 1000px;
            min-height: 100%;
            background: white;
            margin: 0 auto;
            box-shadow: 0 10px 30px rgba(0,0,0,0.08);
            border-collapse: collapse;
            position: relative;
          }

          @media print {
            body {
              background: white;
              padding: 0;
              margin: 0;
            }
            .print-master-table {
              box-shadow: none;
              width: 100%;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            /* Footer Fix at Bottom for Printing */
            .lh-footer-wrapper {
              position: fixed;
              bottom: 0;
              left: 0;
              width: 100%;
            }
            .content-area-cell {
              padding-bottom: 180px;
            }
          }

          @media screen {
            .lh-footer-wrapper {
              position: absolute;
              bottom: 0;
              left: 0;
              width: 100%;
            }
            .content-area-cell {
              padding-bottom: 180px;
            }
          }

          /* COMPACT HEADER STYLING */
          .lh-header-container {
            padding: 20px 30px 10px 30px;
            background: white;
            position: relative;
          }
          .top-accent-bar {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 12px;
            background: linear-gradient(90deg, #103f7a 72%, #1c7c34 72%);
          }
          .lh-header-main {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 5px;
          }
          .lh-logo-container {
            width: 130px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .lh-logo-img {
            width: 100%;
            max-height: 95px;
            object-fit: contain;
          }
          .lh-brand-section {
            flex-grow: 1;
            padding-left: 20px;
          }
          .lh-main-title {
            font-family: 'Montserrat', sans-serif;
            font-size: 23px;
            font-weight: 800;
            color: #e31e24;
            letter-spacing: 0.2px;
            line-height: 1.1;
            margin-bottom: 4px;
          }
          .lh-divider-line {
            height: 1.5px;
            background-color: #103f7a;
            margin-bottom: 6px;
          }
          .lh-services-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 3px 8px;
            max-width: 450px;
          }
          .lh-service-item {
            font-size: 8.5px;
            font-weight: 700;
            color: #0c2340;
            display: flex;
            align-items: center;
          }
          .lh-service-item::after {
            content: "|";
            color: #1c7c34;
            margin-left: 8px;
          }
          .lh-services-grid .lh-service-item:last-child::after {
            content: "";
          }
          .lh-contacts {
            text-align: left;
            border-left: 2px solid #e2e8f0;
            padding-left: 12px;
            min-width: 170px;
          }
          .lh-owner-name {
            font-size: 11px;
            font-weight: 700;
            color: #103f7a;
            margin-bottom: 2px;
          }
          .lh-phone-num {
            font-size: 11px;
            font-weight: 700;
            color: #2d3748;
            display: flex;
            align-items: center;
            gap: 4px;
          }

          /* METADATA INFO BAR */
          .bill-info-bar {
            background: #f8fafc;
            padding: 8px 30px;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            border-top: 1px solid #e2e8f0;
            border-bottom: 1px solid #e2e8f0;
          }
          .info-group {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
          }
          .info-item {
            font-size: 11.5px;
          }
          .info-label {
            font-weight: 600;
            color: #64748b;
          }
          .info-value {
            font-weight: 700;
            color: #1e293b;
            margin-left: 4px;
          }

          /* MAIN BODY CONTENT AREA & WATERMARK */
          .content-area-cell {
            padding: 15px 30px 25px 30px;
            vertical-align: top;
            position: relative;
          }
          .watermark-wrapper {
            position: absolute;
            top: 40%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-12deg);
            width: 40%;
            opacity: 0.04;
            pointer-events: none;
            z-index: 0;
          }
          .watermark-img {
            width: 100%;
            object-fit: contain;
          }
          .section-title-modern {
            font-family: 'Montserrat', sans-serif;
            font-size: 16px;
            font-weight: 700;
            color: #103f7a;
            margin-bottom: 15px;
            padding-bottom: 6px;
            border-bottom: 2px solid #e2e8f0;
            letter-spacing: 0.5px;
          }

          /* PARTY SUMMARY CARD */
          .party-summary-card {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            border-radius: 12px;
            padding: 20px;
            margin-bottom: 25px;
            color: white;
            position: relative;
            z-index: 1;
          }
          .party-summary-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 20px;
          }
          .summary-item {
            text-align: center;
          }
          .summary-label {
            font-size: 12px;
            opacity: 0.9;
            margin-bottom: 8px;
          }
          .summary-value {
            font-size: 24px;
            font-weight: 700;
          }

          /* RECEIPTS TABLE */
          .receipts-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            position: relative;
            z-index: 1;
          }
          .receipts-table th {
            background: #f1f5f9;
            padding: 12px 8px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            color: #334155;
            border-bottom: 2px solid #cbd5e1;
            text-align: left;
          }
          .receipts-table th:last-child {
            text-align: right;
          }
          .receipts-table td {
            font-size: 12px;
            color: #1e293b;
          }

          /* TOTAL SECTION */
          .total-section {
            padding: 15px 20px;
            background: #fffbeb;
            border: 1px solid #fef08a;
            border-radius: 8px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            position: relative;
            z-index: 1;
          }
          .total-label {
            font-size: 14px;
            font-weight: 600;
            color: #64748b;
          }
          .total-amount-large {
            font-size: 22px;
            font-weight: 800;
            color: #2e7d32;
          }

          /* FIXED BOTTOM FOOTER DESIGN */
          .lh-footer-container {
            background: #0d1e3d;
            color: white;
            width: 100%;
          }
          .lh-footer-top-curve {
            background: #103f7a;
            padding: 10px 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-top-left-radius: 30px;
          }
          .lh-footer-address {
            font-size: 10px;
            font-weight: 500;
            max-width: 480px;
            line-height: 1.4;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .lh-footer-email-box {
            background: #1c7c34;
            padding: 5px 12px;
            border-radius: 15px;
            font-size: 10.5px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 5px;
            border-top-left-radius: 0px;
            white-space: nowrap;
          }
          .lh-footer-logos-pane {
            background: white;
            padding: 8px 30px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 15px;
            border-top-left-radius: 30px;
            margin-top: -4px;
          }
          .lh-partner-logo {
            height: 24px;
            object-fit: contain;
          }
          .lh-footer-hindi-bar {
            background: #051429;
            padding: 8px 30px;
            text-align: center;
          }
          .lh-hindi-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 4px 12px;
          }
          .lh-hindi-item {
            font-size: 10px;
            font-weight: 500;
            color: #cbd5e1;
          }
          .lh-hindi-item::after {
            content: "|";
            color: #1c7c34;
            margin-left: 12px;
          }
          .lh-hindi-grid .lh-hindi-item:last-child::after {
            content: "";
          }
          .powered-by-tag {
            font-size: 7.5px;
            color: #94a3b8;
            text-align: center;
            padding: 3px;
            background: #020b17;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }

          /* Badge styles */
          .badge-success {
            background: #e8f5e9;
            color: #2e7d32;
            padding: 4px 8px;
            border-radius: 12px;
            font-size: 11px;
            font-weight: 600;
          }
        </style>
      </head>
      <body>

        <table class="print-master-table">
          
          <thead>
            <tr>
              <td>
                <div class="lh-header-container">
                  <div class="top-accent-bar"></div>
                  <div class="lh-header-main">
                    <div class="lh-logo-container">
                      <img class="lh-logo-img" src="${COMPANY_LOGO}" alt="DK RTO Logo" />
                    </div>
                    
                    <div class="lh-brand-section">
                      <div class="lh-main-title">D.K. RTO & INSURANCE ADVISOR</div>
                      <div class="lh-divider-line"></div>
                      <div class="lh-services-grid">
                        <span class="lh-service-item">DRIVING LICENCE</span>
                        <span class="lh-service-item">INSURANCE</span>
                        <span class="lh-service-item">ROAD TAX</span>
                        <span class="lh-service-item">PERMIT</span>
                        <span class="lh-service-item">NAME TRANSFER</span>
                        <span class="lh-service-item">HSRP NO. PLATE</span>
                        <span class="lh-service-item">FITNESS & OTHER RTO WORK</span>
                      </div>
                    </div>
                    
                    <div class="lh-contacts">
                      <div class="lh-owner-name">Durgesh Kumar Siware</div>
                      <div class="lh-phone-num">📞 79749 97280</div>
                      <div class="lh-phone-num" style="margin-top: 1px;">📞 73890 42205</div>
                    </div>
                  </div>
                </div>
                
                <div class="bill-info-bar">
                  <div class="info-group">
                    <div class="info-item"><span class="info-label">Statement Date:</span><span class="info-value">${formattedDate}</span></div>
                    <div class="info-item"><span class="info-label">Report Type:</span><span class="info-value">Party Wise Receipts</span></div>
                  </div>
                  <div class="info-item"><span class="info-label">Print Date:</span><span class="info-value">${formattedDate}</span></div>
                </div>
              </td>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td class="content-area-cell">
                <div class="watermark-wrapper">
                  <img class="watermark-img" src="${COMPANY_LOGO}" alt="Watermark" />
                </div>
                
                <!-- Party Summary Card -->
                <div class="party-summary-card">
                  <div class="party-summary-grid">
                    <div class="summary-item">
                      <div class="summary-label">Party Name</div>
                      <div class="summary-value" style="font-size: 18px;">${party.partyName}</div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-label">Mobile Number</div>
                      <div class="summary-value" style="font-size: 18px;">${party.partyMobile}</div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-label">Total Receipts</div>
                      <div class="summary-value">${totalReceipts}</div>
                    </div>
                  </div>
                </div>
                
                <div class="section-title-modern">📋 RECEIPT STATEMENT</div>
                
                <table class="receipts-table">
                  <thead>
                    <tr>
                      <th style="width: 5%;">#</th>
                      <th style="width: 25%;">Receipt No. / Description</th>
                      <th style="width: 15%;">Date</th>
                      <th style="width: 15%;">Payment Mode</th>
                      <th style="width: 15%;">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${receiptsRows}
                  </tbody>
                </table>
                
                <div class="total-section">
                  <div class="total-label">GRAND TOTAL</div>
                  <div class="total-amount-large">
                    ₹${formatCurrency(totalAmount)}
                  </div>
                </div>
                
              </td>
            </tr>
          </tbody>

        </table>

        <div class="lh-footer-wrapper">
          <div class="lh-footer-container">
            <div class="lh-footer-top-curve">
              <div class="lh-footer-address">
                📍 Near Babla Fuels, Piyush Seat Cover Road, Parking No. 1, Transport Nagar, Raipur (C.G.) 493221
              </div>
              <div class="lh-footer-email-box">
                ✉ durgeshshivare@gmail.com
              </div>
            </div>
            
            <div class="lh-footer-logos-pane">
              <svg class="lh-partner-logo" viewBox="0 0 100 40" xmlns="http://www.w3.org/2000/svg">
                <rect width="100" height="40" rx="3" fill="#0f4c81"/>
                <circle cx="20" cy="20" r="10" fill="white" opacity="0.2"/>
                <text x="60" y="21" font-family="sans-serif" font-weight="bold" font-size="8.5" fill="white" text-anchor="middle">UNITED INDIA</text>
                <text x="60" y="30" font-family="sans-serif" font-size="6.5" fill="#cbd5e1" text-anchor="middle">INSURANCE</text>
              </svg>
              <svg class="lh-partner-logo" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="17" fill="#103f7a" stroke="#7ac143" stroke-width="1.5"/>
                <path d="M15,12 L18,10 L22,13 L25,11 L28,16 L24,22 L26,28 L20,32 L16,28 L13,24 L16,18 Z" fill="white" opacity="0.85"/>
              </svg>
            </div>

            <div class="lh-footer-hindi-bar">
              <div class="lh-hindi-grid">
                <span class="lh-hindi-item">ड्राइविंग लाइसेंस</span>
                <span class="lh-hindi-item">इंश्योरेंस</span>
                <span class="lh-hindi-item">रोड टैक्स</span>
                <span class="lh-hindi-item">परमिट</span>
                <span class="lh-hindi-item">नाम ट्रांसफर</span>
                <span class="lh-hindi-item">HSRP नं. प्लेट</span>
                <span class="lh-hindi-item">फिटनेस</span>
                <span class="lh-hindi-item">आर.टी.ओ वर्क</span>
              </div>
            </div>
            
            <div class="powered-by-tag">
              powered by yashvitech IT solution
            </div>
          </div>
        </div>

        <script>
          window.onload = function() { 
            window.print();
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const viewReceipt = async (id) => {
    try {
      const response = await getReceiptById(id);
      if (response.success) {
        setSelectedReceipt(response.data);
        setShowModal(true);
      } else {
        message.error('Failed to load receipt details');
      }
    } catch (error) {
      message.error('Failed to load receipt details');
    }
  };

  const handleDeleteReceipt = async (id, receiptNo) => {
    setLoading(true);
    try {
      await deleteReceipt(id);
      message.success(`Receipt ${receiptNo} deleted successfully`);
      await fetchReceipts();
    } catch (error) {
      message.error('Failed to delete receipt');
    } finally {
      setLoading(false);
    }
  };

  const printReceipt = (receipt) => {
    const printWindow = window.open('', '_blank');
    const currentDate = new Date();
    const formattedDate = currentDate.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });

    // Helper function to format currency
    const formatCurrency = (amount) => {
      if (isNaN(amount) || amount === null || amount === undefined) return '0.00';
      return parseFloat(amount).toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    const titleText = 'PAYMENT RECEIPT';
    const refLabel = 'Receipt No';
    const description = receipt.narration || receipt.rec_category || 'Payment Received';
    const receiptDate = receipt.receipt_date || (receipt.date ? new Date(receipt.date).toLocaleDateString('en-IN') : formattedDate);
    const totalAmount = parseFloat(receipt.amount) || 0;

    // Build item row for Receipt
    const itemsRows = `
      <tr style="border-bottom: 1px solid #e2e8f0;">
        <td style="padding: 8px; text-align: center;">1</td>
        <td style="padding: 8px;">
          <div>${description}</div>
          <div style="font-size: 10px; color: #64748b; margin-top: 2px;">Category: ${receipt.rec_category || '-'} | Mode: ${receipt.rec_mode || '-'}</div>
        </td>
        <td style="padding: 8px; text-align: center;">${receiptDate}</td>
        <td style="padding: 8px; text-align: right; font-weight: 600;">₹${formatCurrency(totalAmount)}</td>
      </tr>
    `;

    // Optional Reference No or Bill Number Link display banner
    let paymentSectionHtml = '';
    if (receipt.bill_no || receipt.ref_no) {
      paymentSectionHtml = `
        <div style="background: linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%); border-left: 4px solid #2e7d32; padding: 10px 15px; margin: 15px 0; border-radius: 6px; font-size: 12px;">
          <div style="display: flex; gap: 20px; flex-wrap: wrap;">
            ${receipt.bill_no ? `<span><strong>📄 Against Bill No:</strong> ${receipt.bill_no}</span>` : ''}
            ${receipt.ref_no ? `<span><strong>🔗 Ref/Txn No:</strong> ${receipt.ref_no}</span>` : ''}
            <span><strong>📊 Status:</strong> <span style="color: #2e7d32; font-weight: bold;">Fully Settled</span></span>
          </div>
        </div>
      `;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${titleText} - ${receipt.receipt_no || ''}</title>
        <link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@500;700;800&family=Poppins:wght@400;500;600;700&display=swap" rel="stylesheet">
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          html, body {
            height: 100%;
            background: #eef2f5;
            font-family: 'Poppins', sans-serif;
          }
          body {
            padding: 20px;
            display: flex;
            justify-content: center;
          }
          
          .print-master-table {
            width: 850px;
            min-height: 100%;
            background: white;
            margin: 0 auto;
            box-shadow: 0 10px 30px rgba(0,0,0,0.08);
            border-collapse: collapse;
            position: relative;
          }

          @media print {
            body {
              background: white;
              padding: 0;
              margin: 0;
            }
            .print-master-table {
              box-shadow: none;
              width: 100%;
            }
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .lh-footer-wrapper {
              position: fixed;
              bottom: 0;
              left: 0;
              width: 100%;
            }
            .content-area-cell {
              padding-bottom: 180px;
            }
          }

          @media screen {
            .lh-footer-wrapper {
              position: absolute;
              bottom: 0;
              left: 0;
              width: 100%;
            }
            .content-area-cell {
              padding-bottom: 180px;
            }
          }

          .lh-header-container {
            padding: 20px 30px 10px 30px;
            background: white;
            position: relative;
          }
          .top-accent-bar {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 12px;
            background: linear-gradient(90deg, #103f7a 72%, #1c7c34 72%);
          }
          .lh-header-main {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 5px;
          }
          .lh-logo-container {
            width: 130px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
          }
          .lh-logo-img {
            width: 100%;
            max-height: 95px;
            object-fit: contain;
          }
          .lh-brand-section {
            flex-grow: 1;
            padding-left: 20px;
          }
          .lh-main-title {
            font-family: 'Montserrat', sans-serif;
            font-size: 23px;
            font-weight: 800;
            color: #e31e24;
            letter-spacing: 0.2px;
            line-height: 1.1;
            margin-bottom: 4px;
          }
          .lh-divider-line {
            height: 1.5px;
            background-color: #103f7a;
            margin-bottom: 6px;
          }
          .lh-services-grid {
            display: flex;
            flex-wrap: wrap;
            gap: 3px 8px;
            max-width: 450px;
          }
          .lh-service-item {
            font-size: 8.5px;
            font-weight: 700;
            color: #0c2340;
            display: flex;
            align-items: center;
          }
          .lh-service-item::after {
            content: "|";
            color: #1c7c34;
            margin-left: 8px;
          }
          .lh-services-grid .lh-service-item:last-child::after {
            content: "";
          }
          .lh-contacts {
            text-align: left;
            border-left: 2px solid #e2e8f0;
            padding-left: 12px;
            min-width: 170px;
          }
          .lh-owner-name {
            font-size: 11px;
            font-weight: 700;
            color: #103f7a;
            margin-bottom: 2px;
          }
          .lh-phone-num {
            font-size: 11px;
            font-weight: 700;
            color: #2d3748;
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .bill-info-bar {
            background: #f8fafc;
            padding: 8px 30px;
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            border-top: 1px solid #e2e8f0;
            border-bottom: 1px solid #e2e8f0;
          }
          .info-group {
            display: flex;
            gap: 20px;
            flex-wrap: wrap;
          }
          .info-item {
            font-size: 11.5px;
          }
          .info-label {
            font-weight: 600;
            color: #64748b;
          }
          .info-value {
            font-weight: 700;
            color: #1e293b;
            margin-left: 4px;
          }

          .content-area-cell {
            padding: 15px 30px 25px 30px;
            vertical-align: top;
            position: relative;
          }
          .watermark-wrapper {
            position: absolute;
            top: 40%;
            left: 50%;
            transform: translate(-50%, -50%) rotate(-12deg);
            width: 40%;
            opacity: 0.04;
            pointer-events: none;
            z-index: 0;
          }
          .watermark-img {
            width: 100%;
            object-fit: contain;
          }
          .section-title-modern {
            font-family: 'Montserrat', sans-serif;
            font-size: 14px;
            font-weight: 700;
            color: #103f7a;
            margin-bottom: 12px;
            padding-bottom: 4px;
            border-bottom: 2px solid #e2e8f0;
            letter-spacing: 0.5px;
          }

          .items-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 15px;
            position: relative;
            z-index: 1;
          }
          .items-table th {
            background: #f1f5f9;
            padding: 10px 8px;
            font-size: 11px;
            font-weight: 700;
            text-transform: uppercase;
            color: #334155;
            border-bottom: 2px solid #cbd5e1;
            text-align: left;
          }
          .items-table th:last-child {
            text-align: right;
          }
          .items-table td {
            font-size: 12px;
            color: #1e293b;
          }

          .total-section {
            padding: 12px 15px;
            background: #fffbeb;
            border: 1px solid #fef08a;
            border-radius: 4px;
            display: flex;
            justify-content: flex-end;
            margin-bottom: 15px;
            position: relative;
            z-index: 1;
          }
          .total-amount-large {
            font-size: 18px;
            font-weight: 800;
            color: #103f7a;
          }
          .total-amount-large span {
            font-size: 12px;
            font-weight: 600;
            color: #64748b;
            margin-right: 6px;
          }

          .lh-footer-container {
            background: #0d1e3d;
            color: white;
            width: 100%;
          }
          .lh-footer-top-curve {
            background: #103f7a;
            padding: 10px 30px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-top-left-radius: 30px;
          }
          .lh-footer-address {
            font-size: 10px;
            font-weight: 500;
            max-width: 480px;
            line-height: 1.4;
            display: flex;
            align-items: center;
            gap: 6px;
          }
          .lh-footer-email-box {
            background: #1c7c34;
            padding: 5px 12px;
            border-radius: 15px;
            font-size: 10.5px;
            font-weight: 600;
            display: flex;
            align-items: center;
            gap: 5px;
            border-top-left-radius: 0px;
            white-space: nowrap;
          }
          .lh-footer-logos-pane {
            background: white;
            padding: 8px 30px;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            gap: 15px;
            border-top-left-radius: 30px;
            margin-top: -4px;
          }
          .lh-partner-logo {
            height: 24px;
            object-fit: contain;
          }
          .lh-footer-hindi-bar {
            background: #051429;
            padding: 8px 30px;
            text-align: center;
          }
          .lh-hindi-grid {
            display: flex;
            flex-wrap: wrap;
            justify-content: center;
            gap: 4px 12px;
          }
          .lh-hindi-item {
            font-size: 10px;
            font-weight: 500;
            color: #cbd5e1;
          }
          .lh-hindi-item::after {
            content: "|";
            color: #1c7c34;
            margin-left: 12px;
          }
          .lh-hindi-grid .lh-hindi-item:last-child::after {
            content: "";
          }
          .powered-by-tag {
            font-size: 7.5px;
            color: #94a3b8;
            text-align: center;
            padding: 3px;
            background: #020b17;
            text-transform: uppercase;
            letter-spacing: 0.5px;
          }
        </style>
      </head>
      <body>

        <table class="print-master-table">
          
          <thead>
            <tr>
              <td>
                <div class="lh-header-container">
                  <div class="top-accent-bar"></div>
                  <div class="lh-header-main">
                    <div class="lh-logo-container">
                      <img class="lh-logo-img" src="${COMPANY_LOGO}" alt="DK RTO Logo" />
                    </div>
                    
                    <div class="lh-brand-section">
                      <div class="lh-main-title">D.K. RTO & INSURANCE ADVISOR</div>
                      <div class="lh-divider-line"></div>
                      <div class="lh-services-grid">
                        <span class="lh-service-item">DRIVING LICENCE</span>
                        <span class="lh-service-item">INSURANCE</span>
                        <span class="lh-service-item">ROAD TAX</span>
                        <span class="lh-service-item">PERMIT</span>
                        <span class="lh-service-item">NAME TRANSFER</span>
                        <span class="lh-service-item">HSRP NO. PLATE</span>
                        <span class="lh-service-item">FITNESS & OTHER RTO WORK</span>
                      </div>
                    </div>
                    
                    <div class="lh-contacts">
                      <div class="lh-owner-name">Durgesh Kumar Siware</div>
                      <div class="lh-phone-num">📞 79749 97280</div>
                      <div class="lh-phone-num" style="margin-top: 1px;">📞 73890 42205</div>
                    </div>
                  </div>
                </div>
                
                <div class="bill-info-bar">
                  <div class="info-group">
                    <div class="info-item"><span class="info-label">${refLabel}:</span><span class="info-value">${receipt.receipt_no || '-'}</span></div>
                    <div class="info-item"><span class="info-label">Date:</span><span class="info-value">${receiptDate}</span></div>
                    <div class="info-item"><span class="info-label">Received From:</span><span class="info-value">${receipt.received_from || '-'}</span></div>
                    <div class="info-item"><span class="info-label">Mobile:</span><span class="info-value">${receipt.mobile_no || receipt.party_mobile || 'N/A'}</span></div>
                  </div>
                  <div class="info-item"><span class="info-label">Print Date:</span><span class="info-value">${formattedDate}</span></div>
                </div>
              </td>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td class="content-area-cell">
                <div class="watermark-wrapper">
                  <img class="watermark-img" src="${COMPANY_LOGO}" alt="Watermark" />
                </div>
                
                <div class="section-title-modern">${titleText}</div>
                
                <table class="items-table">
                  <thead>
                    <tr>
                      <th style="width: 8%;">#</th>
                      <th style="width: 40%;">Description / Purpose</th>
                      <th style="width: 15%;">Receipt Date</th>
                      <th style="width: 15%;">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${itemsRows}
                  </tbody>
                </table>
                
                <div class="total-section">
                  <div class="total-amount-large">
                    <span>TOTAL RECEIVED:</span> ₹${formatCurrency(totalAmount)}
                  </div>
                </div>
                
                <div class="payment-details-card">
                  ${paymentSectionHtml}
                </div>
              </td>
            </tr>
          </tbody>

        </table>

        <div class="lh-footer-wrapper">
          <div class="lh-footer-container">
            <div class="lh-footer-top-curve">
              <div class="lh-footer-address">
                📍 Near Babla Fuels, Piyush Seat Cover Road, Parking No. 1, Transport Nagar, Raipur (C.G.) 493221
              </div>
              <div class="lh-footer-email-box">
                ✉ durgeshshivare@gmail.com
              </div>
            </div>
            
            <div class="lh-footer-logos-pane">
              <svg class="lh-partner-logo" viewBox="0 0 100 40" xmlns="http://www.w3.org/2000/svg">
                <rect width="100" height="40" rx="3" fill="#0f4c81"/>
                <circle cx="20" cy="20" r="10" fill="white" opacity="0.2"/>
                <text x="60" y="21" font-family="sans-serif" font-weight="bold" font-size="8.5" fill="white" text-anchor="middle">UNITED INDIA</text>
                <text x="60" y="30" font-family="sans-serif" font-size="6.5" fill="#cbd5e1" text-anchor="middle">INSURANCE</text>
              </svg>
              <svg class="lh-partner-logo" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <circle cx="20" cy="20" r="17" fill="#103f7a" stroke="#7ac143" stroke-width="1.5"/>
                <path d="M15,12 L18,10 L22,13 L25,11 L28,16 L24,22 L26,28 L20,32 L16,28 L13,24 L16,18 Z" fill="white" opacity="0.85"/>
              </svg>
            </div>

            <div class="lh-footer-hindi-bar">
              <div class="lh-hindi-grid">
                <span class="lh-hindi-item">ड्राइविंग लाइसेंस</span>
                <span class="lh-hindi-item">इंश्योरेंस</span>
                <span class="lh-hindi-item">रोड टैक्स</span>
                <span class="lh-hindi-item">परमिट</span>
                <span class="lh-hindi-item">नाम ट्रांसफर</span>
                <span class="lh-hindi-item">HSRP नं. प्लेट</span>
                <span class="lh-hindi-item">फिटनेस</span>
                <span class="lh-hindi-item">आर.टी.ओ वर्क</span>
              </div>
            </div>
            
            <div class="powered-by-tag">
              powered by yashvitech IT solution
            </div>
          </div>
        </div>

        <script>
          window.onload = function() { 
            window.print();
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const exportPartyWiseToExcel = () => {
    setExportLoading(true);
    try {
      if (partyWiseData.length === 0) {
        message.warning("No party data to export!");
        setExportLoading(false);
        return;
      }

      const excelData = partyWiseData.map(party => ({
        "Party Name": party.partyName,
        "Mobile Number": party.partyMobile,
        "Total Receipts": party.receiptCount,
        "Total Amount (₹)": party.totalAmount,
        "Last Receipt Date": party.lastReceiptDate || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Party_Wise_Receipts");
      XLSX.writeFile(wb, `Party_Wise_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success("Export successful!");
    } catch (err) {
      console.error(err);
      message.error("Export failed!");
    } finally {
      setExportLoading(false);
    }
  };

  const exportToExcel = () => {
    setExportLoading(true);
    try {
      if (receipts.length === 0) {
        message.warning("No data to export!");
        setExportLoading(false);
        return;
      }

      const excelData = receipts.map(receipt => ({
        "Receipt No": receipt.receipt_no || '-',
        "Date": receipt.receipt_date || '-',
        "Received From": receipt.received_from || '-',
        "Payment Mode": receipt.rec_mode || '-',
        "Category": receipt.rec_category || '-',
        "Reference No": receipt.ref_no || '-',
        "Amount (₹)": receipt.amount || '0',
        "Narration": receipt.narration || '-'
      }));

      const ws = XLSX.utils.json_to_sheet(excelData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Receipts");
      XLSX.writeFile(wb, `Receipts_Report_${new Date().toISOString().slice(0, 10)}.xlsx`);
      message.success("Export successful!");
    } catch (err) {
      console.error(err);
      message.error("Export failed!");
    } finally {
      setExportLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatAmount = (amount) => {
    return `₹${parseFloat(amount).toLocaleString('en-IN')}`;
  };

  const totalAmount = receipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
  const totalPartyAmount = partyWiseData.reduce((sum, p) => sum + p.totalAmount, 0);
  const totalParties = partyWiseData.length;

  // Filter parties based on search
  const filteredParties = partyWiseData.filter(party =>
    party.partyName.toLowerCase().includes(partySearch.toLowerCase()) ||
    party.partyMobile.includes(partySearch)
  );

  // Mobile card render for All Receipts
  const renderReceiptCard = (receipt, index) => {
    return (
      <Card key={receipt.id} style={{ marginBottom: 12, borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <Tag color="blue">{receipt.receipt_no || 'Auto'}</Tag>
            <Tag color="geekblue" style={{ marginLeft: 4 }}>{formatDate(receipt.receipt_date)}</Tag>
          </div>
          <Popconfirm
            title="Delete Receipt"
            description={`Delete ${receipt.receipt_no || 'receipt'}?`}
            onConfirm={() => handleDeleteReceipt(receipt.id, receipt.receipt_no)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
        <div style={{ marginBottom: 8 }}>
          <UserOutlined style={{ color: '#52c41a', marginRight: 8 }} />
          <Text strong>{receipt.received_from}</Text>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tag icon={<CreditCardOutlined />} color="purple">{receipt.rec_mode}</Tag>
          <Text strong style={{ color: '#52c41a', fontSize: 16 }}>{formatAmount(receipt.amount)}</Text>
        </div>
      </Card>
    );
  };

  // Mobile card render for Party Wise
  const renderPartyCard = (party, index) => {
    return (
      <Card key={party.partyName} style={{ marginBottom: 12, borderRadius: 10 }} bodyStyle={{ padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
          <div>
            <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff', marginRight: 8 }} size="small" />
            <Text strong>{party.partyName}</Text>
          </div>
          {/* <Button size="small" icon={<EyeOutlined />} onClick={() => viewPartyDetails(party)}>Details</Button> */}
        </div>
        <div style={{ marginBottom: 8 }}>
          <Tag icon={<PhoneOutlined />} color="cyan">{party.partyMobile}</Tag>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Tag color="geekblue">Receipts: {party.receiptCount}</Tag>
          <Text strong style={{ color: '#52c41a', fontSize: 14 }}>{formatAmount(party.totalAmount)}</Text>
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 6 }}>Last: {formatDate(party.lastReceiptDate)}</div>
      </Card>
    );
  };

  const columns = [
    {
      title: 'SR NO.',
      key: 'srno',
      width: 70,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Receipt No.',
      dataIndex: 'receipt_no',
      key: 'receipt_no',
      render: (text) => <Tag color="blue">{text || 'Auto'}</Tag>
    },
    {
      title: 'Date',
      dataIndex: 'receipt_date',
      key: 'receipt_date',
      render: (text) => <Tag color="geekblue">{formatDate(text)}</Tag>
    },
    {
      title: 'Received From',
      dataIndex: 'received_from',
      key: 'received_from',
      render: (text) => (
        <Space>
          <UserOutlined style={{ color: '#52c41a' }} />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: 'Payment Mode',
      dataIndex: 'rec_mode',
      key: 'rec_mode',
      render: (text) => (
        <Tag icon={<CreditCardOutlined />} color="purple">
          {text}
        </Tag>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      render: (text) => (
        <Text strong style={{ color: '#52c41a' }}>
          {formatAmount(text)}
        </Text>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Delete Receipt"
          description={`Delete ${record.receipt_no || 'receipt'}?`}
          onConfirm={() => handleDeleteReceipt(record.id, record.receipt_no)}
          okText="Yes"
          cancelText="No"
        >
          <Tooltip title="Delete">
            <Button
              type="link"
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Tooltip>
        </Popconfirm>
      )
    }
  ];

  const partyWiseColumns = [
    {
      title: 'SR NO.',
      key: 'srno',
      width: 70,
      render: (_, __, index) => index + 1
    },
    {
      title: 'Party Name',
      dataIndex: 'partyName',
      key: 'partyName',
      render: (text, record) => (
        <Space>
          <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
          <Button type="link" onClick={() => viewPartyDetails(record)} style={{ padding: 0 }}>
            {text}
          </Button>
        </Space>
      )
    },
    {
      title: 'Mobile Number',
      dataIndex: 'partyMobile',
      key: 'partyMobile',
      render: (text) => <Tag icon={<PhoneOutlined />} color="cyan">{text}</Tag>
    },
    {
      title: 'Receipt Count',
      dataIndex: 'receiptCount',
      key: 'receiptCount',
      align: 'center',
      render: (text) => <Tag color="geekblue">{text}</Tag>
    },
    {
      title: 'Total Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      align: 'right',
      render: (text) => (
        <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
          {formatAmount(text)}
        </Text>
      )
    },
    {
      title: 'Last Receipt',
      dataIndex: 'lastReceiptDate',
      key: 'lastReceiptDate',
      render: (text) => formatDate(text)
    }
  ];

  return (
    <div style={{ minHeight: '100vh' }}>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Total Receipts"
              value={activeTab === 'all' ? receipts.length : totalParties}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Total Amount"
              value={activeTab === 'all' ? totalAmount : totalPartyAmount}
              prefix="₹"
              valueStyle={{ color: '#52c41a' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title="Average Amou"
              value={activeTab === 'all' ? (receipts.length > 0 ? totalAmount / receipts.length : 0) : (totalParties > 0 ? totalPartyAmount / totalParties : 0)}
              prefix="₹"
              precision={2}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <Statistic
              title={activeTab === 'all' ? "Unique Parties" : "Total Parties"}
              value={totalParties}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Filter Section */}
      <Card style={{ marginBottom: 24, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: '20px' }}>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} sm={12} md={8}>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>Search</Text>
              <Input
                placeholder="Search by receipt no or party..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onPressEnter={handleSearch}
                allowClear
                size="middle"
                prefix={<SearchOutlined />}
              />
            </div>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>Date Range</Text>
              <RangePicker
                style={{ width: '100%' }}
                size="middle"
                onChange={handleDateRangeChange}
                placeholder={['Start Date', 'End Date']}
                value={dateRange}
              />
            </div>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <Space size="middle" wrap>
              <Button
                type="primary"
                icon={<SearchOutlined />}
                onClick={handleSearch}
                loading={loading}
                size="middle"
              >
                Search
              </Button>
              <Button onClick={handleReset} size="middle">
                Clear All
              </Button>
              <Button
                icon={<DownloadOutlined />}
                onClick={activeTab === 'all' ? exportToExcel : exportPartyWiseToExcel}
                loading={exportLoading}
                size="middle"
              >
                Export
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Tabs Section */}
      <Card style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }} bodyStyle={{ padding: 0 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} type="card" style={{ padding: '0 16px' }}>
          <TabPane
            tab={<span><FileTextOutlined /> All Receipts</span>}
            key="all"
          >
            {!isMobile ? (
              <Table
                columns={columns}
                dataSource={receipts}
                rowKey="id"
                loading={loading}
                pagination={{
                  pageSize: 10,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} receipts`,
                  showSizeChanger: true,
                  showQuickJumper: true
                }}
                size="middle"
                bordered
              />
            ) : (
              <div style={{ padding: '12px', maxHeight: '70vh', overflowY: 'auto' }}>
                {receipts
                  .slice(
                    (receiptPage - 1) * mobilePageSize,
                    receiptPage * mobilePageSize
                  )
                  .map((receipt, idx) => renderReceiptCard(receipt, idx))}

                <Pagination
                  current={receiptPage}
                  pageSize={mobilePageSize}
                  total={receipts.length}
                  onChange={setReceiptPage}
                  showSizeChanger={false}
                  size="small"
                  style={{
                    textAlign: "center",
                    marginTop: 15
                  }}
                />
              </div>
            )}
          </TabPane>

          <TabPane
            tab={<span><BarChartOutlined /> Party Wise Report</span>}
            key="partyWise"
          >
            <div style={{ padding: '16px 0' }}>
              <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={8}>
                  <Input
                    placeholder="Search party by name or mobile..."
                    prefix={<SearchOutlined />}
                    value={partySearch}
                    onChange={(e) => setPartySearch(e.target.value)}
                    allowClear
                  />
                </Col>
                <Col span={16} style={{ textAlign: 'right' }}>
                  <Text type="secondary">
                    Total Parties: {filteredParties.length} | Total Amount: {formatAmount(filteredParties.reduce((sum, p) => sum + p.totalAmount, 0))}
                  </Text>
                </Col>
              </Row>

              {!isMobile ? (
                <Table
                  columns={partyWiseColumns}
                  dataSource={filteredParties}
                  rowKey="partyName"
                  pagination={{
                    pageSize: 10,
                    showTotal: (total) => `Total ${total} parties`,
                    showSizeChanger: true
                  }}
                  size="middle"
                  bordered
                />
              ) : (
                <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  {filteredParties.map((party, idx) => renderPartyCard(party, idx))}
                </div>
              )}
            </div>
          </TabPane>
        </Tabs>
      </Card>

      {/* View Receipt Modal */}
      <Modal
        title={
          <Space>
            <FileTextOutlined style={{ color: '#1890ff' }} />
            <span>Receipt Details</span>
            <Tag color="blue">{selectedReceipt?.receipt_no}</Tag>
          </Space>
        }
        open={showModal}
        onCancel={() => setShowModal(false)}
        width={600}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowModal(false)}>
            Close
          </Button>
        ]}
      >
        {selectedReceipt && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Receipt No">{selectedReceipt.receipt_no || '-'}</Descriptions.Item>
              <Descriptions.Item label="Date">{formatDate(selectedReceipt.receipt_date)}</Descriptions.Item>
              <Descriptions.Item label="Received From" span={2}>{selectedReceipt.received_from}</Descriptions.Item>
              <Descriptions.Item label="Party Name">{selectedReceipt.party_name || '-'}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{selectedReceipt.party_mobile || '-'}</Descriptions.Item>
              <Descriptions.Item label="Payment Mode">{selectedReceipt.rec_mode}</Descriptions.Item>
              <Descriptions.Item label="Category">{selectedReceipt.rec_category || '-'}</Descriptions.Item>
              {selectedReceipt.ref_no && (
                <Descriptions.Item label="Reference No" span={2}>{selectedReceipt.ref_no}</Descriptions.Item>
              )}
              <Descriptions.Item label="Amount" span={2}>
                <Text strong style={{ color: '#52c41a', fontSize: 16 }}>
                  {formatAmount(selectedReceipt.amount)}
                </Text>
              </Descriptions.Item>
              {selectedReceipt.narration && (
                <Descriptions.Item label="Narration" span={2}>{selectedReceipt.narration}</Descriptions.Item>
              )}
            </Descriptions>
          </div>
        )}
      </Modal>

      {/* Party Details Modal */}
      <Modal
        title={
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <span>Party Receipts</span>
            <Tag color="blue">{partyReceipts[0]?.received_from}</Tag>
          </Space>
        }
        open={showPartyModal}
        onCancel={() => setShowPartyModal(false)}
        width={900}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowPartyModal(false)}>
            Close
          </Button>
        ]}
      >
        {partyReceipts.length > 0 && (
          <div>
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 20 }}>
              <Descriptions.Item label="Party Name">{partyReceipts[0]?.received_from}</Descriptions.Item>
              <Descriptions.Item label="Mobile">{partyReceipts[0]?.party_mobile || '-'}</Descriptions.Item>
              <Descriptions.Item label="Total Receipts">{partyReceipts.length}</Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                <Text strong style={{ color: '#52c41a' }}>
                  {formatAmount(partyReceipts.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0))}
                </Text>
              </Descriptions.Item>
            </Descriptions>

            <Table
              columns={[
                {
                  title: 'Receipt No',
                  dataIndex: 'receipt_no',
                  key: 'receipt_no',
                  render: (text) => <Tag color="blue">{text || 'Auto'}</Tag>
                },
                {
                  title: 'Date',
                  dataIndex: 'receipt_date',
                  key: 'receipt_date',
                  render: (text) => formatDate(text)
                },
                {
                  title: 'Payment Mode',
                  dataIndex: 'rec_mode',
                  key: 'rec_mode',
                  render: (text) => <Tag color="purple">{text}</Tag>
                },
                {
                  title: 'Amount',
                  dataIndex: 'amount',
                  key: 'amount',
                  align: 'right',
                  render: (text) => (
                    <Text strong style={{ color: '#52c41a' }}>
                      {formatAmount(text)}
                    </Text>
                  )
                }
              ]}
              dataSource={partyReceipts}
              rowKey="id"
              size="small"
              pagination={false}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PaymentInReport;